import React, { useState } from "react";
import { Box, Paper, Typography, TextField, IconButton } from "@mui/material";
import {
  TrackChanges as TargetIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import type { ClientObjective } from "./types";

interface ClientObjectivesSectionProps {
  objectives: ClientObjective[];
  onAddObjective: (content: string) => void;
  onDeleteObjective: (id: string) => void;
}

const ClientObjectivesSection: React.FC<ClientObjectivesSectionProps> = ({
  objectives,
  onAddObjective,
  onDeleteObjective,
}) => {
  const { t } = useTranslation();
  const [newObjective, setNewObjective] = useState("");

  const handleAdd = () => {
    if (newObjective.trim()) {
      onAddObjective(newObjective.trim());
      setNewObjective("");
    }
  };

  return (
    <Paper sx={{ p: 2.5, bgcolor: "#fff", borderRadius: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
        <TargetIcon color="primary" fontSize="small" />
        <Typography
          variant="subtitle1"
          sx={{
            color: "text.secondary",
            mb: 0,
            textTransform: "uppercase"
          }}>
          {t("coachingNavigator.clientOverview.clientObjectives")}
        </Typography>
      </Box>
      <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
        <TextField
          value={newObjective}
          onChange={(e) => setNewObjective(e.target.value)}
          placeholder={t("coachingNavigator.clientOverview.addObjectivePlaceholder")}
          multiline
          rows={2}
          fullWidth
          sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
        />
        <IconButton
          onClick={handleAdd}
          disabled={!newObjective.trim()}
          color="primary"
          sx={{ alignSelf: "stretch", aspectRatio: 1 }}
        >
          <AddIcon />
        </IconButton>
      </Box>
      <Typography
        variant="body2"
        sx={{
          color: "text.secondary",
          mb: 1
        }}>
        {t("coachingNavigator.clientOverview.history")}
      </Typography>
      <Box sx={{ maxHeight: 140, overflow: "auto" }}>
        {objectives.length === 0 ? (
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              fontStyle: "italic"
            }}>
            {t("coachingNavigator.clientOverview.noObjectivesYet")}
          </Typography>
        ) : (
          objectives.map((obj) => (
            <Box
              key={obj.id}
              sx={{
                borderLeft: 2,
                borderColor: "divider",
                pl: 1.5,
                py: 1,
                mb: 1,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="caption"
                  sx={{
                    color: "text.secondary",
                    textTransform: "uppercase",
                    letterSpacing: 0.5
                  }}>
                  {dayjs(obj.timestamp).format("MMM D, YYYY • HH:mm")}
                </Typography>
                <Typography variant="body2">{obj.content}</Typography>
              </Box>
              <IconButton
                size="small"
                color="error"
                onClick={() => onDeleteObjective(obj.id)}
                sx={{ ml: 1, flexShrink: 0 }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          ))
        )}
      </Box>
    </Paper>
  );
};

export default ClientObjectivesSection;
